import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { offset } from "@floating-ui/dom";
import React, { useState, useRef, useEffect } from "react";
import { RightSquareOutlined, YoutubeOutlined } from "@ant-design/icons";
import { getHeroIcon } from "../../_helpers/heroicons";
import LinkModal from "./LinkModal";
import YoutubeModal from "./YoutubeModal";
import TableControls from "./TableControls";
import HeadingDropdown from "./HeadingDropdown";
import HighlightDropdown from "./HighlightDropdown";
import TextColour from "./TextColour";
import ListDropdown from "./ListDropdown";
import AlignmentDropdown from "./AlignmentDropdown";
import styles from "../styles/QurealRichEditor.module.css";

const FEATURE_SETS = {
    all: [
        "undo/redo",
        "headings",
        "paragraphStyles",
        "lists",
        "table",
        "hardbreak",
        "textFormat",
        "textAlign",
        "highlight",
        "extra",
        "media",
        "colors",
        "theme",
    ],
    basic: ["textFormat", "textAlign", "colors", "highlight"],
};
function BubbleMenuBar({ editor, toggleTheme, darkMode, features = "all" }) {
    const imageInputRef = useRef(null);

    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [tableControlsOpen, setTableControlsOpen] = useState(false);
    const [height, setHeight] = useState(480);
    const [width, setWidth] = useState(640);
    const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
    const [selectionKey, setSelectionKey] = useState(0);

    const featuresToShow =
        typeof features === "string"
            ? FEATURE_SETS[features] || FEATURE_SETS.all
            : Array.isArray(features)
            ? features
            : FEATURE_SETS.all;

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add("dark-mode");
        } else {
            document.body.classList.remove("dark-mode");
        }
    }, [darkMode]);
    useEffect(() => {
        if (!editor) return;

        // Force a component update when selection changes (v3 specific)
        const handleSelectionUpdate = () => {
            // This forces a React re-render to update button states
            setSelectionKey((prev) => prev + 1);
        };

        // v3 uses these event names
        editor.on("selectionUpdate", handleSelectionUpdate);
        editor.on("focus", handleSelectionUpdate);
        editor.on("transaction", handleSelectionUpdate);

        return () => {
            editor.off("selectionUpdate", handleSelectionUpdate);
            editor.off("focus", handleSelectionUpdate);
            editor.off("transaction", handleSelectionUpdate);
        };
    }, [editor]);

    if (!editor) {
        return null;
    }

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                editor.chain().focus().setImage({ src: e.target.result }).run();
            };
            reader.readAsDataURL(file);
            event.target.value = "";
        }
    };

    const openYoutubeModal = () => {
        setYoutubeModalOpen(true);
    };

    const handleYoutubeConfirm = (url, width, height) => {
        if (url) {
            editor.commands.setYoutubeVideo({
                src: url,
                width: Math.max(320, width),
                height: Math.max(180, height),
            });
        }
        setYoutubeModalOpen(false);
    };

    const openLinkModal = () => {
        const { from, to } = editor.state.selection;
        const text = editor.state.doc.textBetween(from, to, "");
        const urlRegex = /^(https?:\/\/|www\.)[^\s]+$/i;
        const isUrl = text && urlRegex.test(text);

        if (isUrl && !editor.isActive("link")) {
            editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: text })
                .run();
        }
        setLinkModalOpen(true);
    };

    const handleLinkConfirm = (url) => {
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
        } else {
            const formattedUrl = url.trim();
            const hasProtocol = /^(https?:\/\/|mailto:|tel:)/i.test(
                formattedUrl
            );
            const urlWithProtocol = hasProtocol
                ? formattedUrl
                : `https://${formattedUrl}`;

            editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: urlWithProtocol })
                .run();

            setTimeout(() => {
                const linkButton = document.querySelector(
                    'button[title="Link"]'
                );
                if (linkButton) {
                    linkButton.classList.add("link-success");
                    setTimeout(
                        () => linkButton.classList.remove("link-success"),
                        1000
                    );
                }
            }, 100);
        }
        setLinkModalOpen(false);
    };
    return (
        <>
            <BubbleMenu
                className={`tiptap-bubble-menu ${styles.controlGroup}`}
                editor={editor}
                options={{
                    middleware: [offset(6)],
                    duration: 150,
                    placement: "top",
                    interactive: true,
                    maxWidth: "none",
                    animation: "bubbleAppear", // Use your custom animation
                }}
            >
                <div className={styles.buttonGroup}>
                    {featuresToShow.includes("undo/redo") && (
                        <>
                            <button
                                onClick={() =>
                                    editor.chain().focus().undo().run()
                                }
                                title="Undo"
                                className={styles.button}
                            >
                                {getHeroIcon("arrow-uturn-down-left", {
                                    width: "18px",
                                })}
                            </button>
                            <button
                                onClick={() =>
                                    editor.chain().focus().redo().run()
                                }
                                title="Redo"
                                className={styles.button}
                            >
                                {getHeroIcon("arrow-uturn-down-right", {
                                    width: "18px",
                                })}
                            </button>
                        </>
                    )}
                    {featuresToShow.includes("headings") && (
                        <>
                            <HeadingDropdown editor={editor} />
                        </>
                    )}

                    {/* Lists */}
                    {featuresToShow.includes("lists") && (
                        <>
                            <ListDropdown editor={editor} />
                        </>
                    )}

                    {featuresToShow.includes("paragraphStyles") && (
                        <>
                            <button
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleCodeBlock()
                                        .run()
                                }
                                className={
                                    editor.isActive("codeBlock")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Code block"
                            >
                                {getHeroIcon("code-bracket-square", {
                                    width: "24px",
                                })}
                            </button>
                            <button
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleBlockquote()
                                        .run()
                                }
                                className={
                                    editor.isActive("blockquote")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Blockquote"
                            >
                                <RightSquareOutlined />
                            </button>
                        </>
                    )}

                    {/* Table Controls */}
                    {featuresToShow.includes("table") && (
                        <>
                            <div className={styles.tableControlsContainer}>
                                <TableControls
                                    editor={editor}
                                    isOpen={tableControlsOpen}
                                    setIsOpen={setTableControlsOpen}
                                />
                            </div>
                        </>
                    )}
                    {featuresToShow.includes("hardbreak") && (
                        <button
                            onClick={() =>
                                editor.chain().focus().setHardBreak().run()
                            }
                            className={
                                editor.isActive("hardbreak")
                                    ? styles.isActive
                                    : styles.button
                            }
                            title="hard break"
                        >
                            {getHeroIcon("arrow-turn-down-left", {
                                width: "18px",
                            })}
                        </button>
                    )}
                    {/* Text Formatting */}
                    {featuresToShow.includes("textFormat") && (
                        <>
                            <button
                                onClick={() =>
                                    editor.chain().focus().toggleBold().run()
                                }
                                disabled={
                                    !editor
                                        .can()
                                        .chain()
                                        .focus()
                                        .toggleBold()
                                        .run()
                                }
                                className={
                                    editor.isActive("bold")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Bold"
                            >
                                {getHeroIcon("bold", { width: "18px" })}
                            </button>
                            <button
                                onClick={() =>
                                    editor.chain().focus().toggleItalic().run()
                                }
                                disabled={
                                    !editor
                                        .can()
                                        .chain()
                                        .focus()
                                        .toggleItalic()
                                        .run()
                                }
                                className={
                                    editor.isActive("italic")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Italic"
                            >
                                {getHeroIcon("italic", { width: "18px" })}
                            </button>
                            <button
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleUnderline()
                                        .run()
                                }
                                disabled={
                                    !editor
                                        .can()
                                        .chain()
                                        .focus()
                                        .toggleUnderline()
                                        .run()
                                }
                                className={
                                    editor.isActive("underline")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Underline"
                            >
                                {getHeroIcon("underline", { width: "18px" })}
                            </button>
                            <button
                                onClick={() =>
                                    editor.chain().focus().toggleStrike().run()
                                }
                                disabled={
                                    !editor
                                        .can()
                                        .chain()
                                        .focus()
                                        .toggleStrike()
                                        .run()
                                }
                                className={
                                    editor.isActive("strike")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Strike"
                            >
                                {getHeroIcon("strikethrough", {
                                    width: "18px",
                                })}
                            </button>
                        </>
                    )}

                    {/* More formatting options */}
                    {featuresToShow.includes("extra") && (
                        <>
                            <button
                                onClick={() =>
                                    editor.chain().focus().setParagraph().run()
                                }
                                className={
                                    editor.isActive("paragraph")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Paragraph"
                            >
                                {getHeroIcon("document-text", {
                                    width: "18px",
                                })}
                            </button>
                            <button
                                onClick={() =>
                                    editor.chain().focus().toggleCode().run()
                                }
                                disabled={
                                    !editor
                                        .can()
                                        .chain()
                                        .focus()
                                        .toggleCode()
                                        .run()
                                }
                                className={
                                    editor.isActive("code")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Inline Code"
                            >
                                {getHeroIcon("code-bracket", { width: "18px" })}
                            </button>
                        </>
                    )}

                    {featuresToShow.includes("highlight") && (
                        <>
                            <HighlightDropdown editor={editor} />
                        </>
                    )}

                    {featuresToShow.includes("media") && (
                        <>
                            <button
                                onClick={openLinkModal}
                                className={
                                    editor.isActive("link")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Link"
                            >
                                {getHeroIcon("link", { width: "18px" })}
                            </button>
                        </>
                    )}

                    {/* Super/subscript and text alignment */}
                    {featuresToShow.includes("extra") && (
                        <>
                            <button
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleSuperscript()
                                        .run()
                                }
                                className={
                                    editor.isActive("superscript")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Superscript"
                            >
                                X²
                            </button>
                            <button
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleSubscript()
                                        .run()
                                }
                                className={
                                    editor.isActive("subscript")
                                        ? styles.isActive
                                        : styles.button
                                }
                                title="Subscript"
                            >
                                X₂
                            </button>
                        </>
                    )}

                    {featuresToShow.includes("textAlign") && (
                        <>
                            <AlignmentDropdown editor={editor} />
                        </>
                    )}

                    {/* Color, image and media */}
                    {featuresToShow.includes("colors") && (
                        <>
                            <TextColour editor={editor} />
                        </>
                    )}

                    {featuresToShow.includes("media") && (
                        <>
                            <button
                                onClick={() => imageInputRef.current.click()}
                                title="Upload Image"
                                className={styles.button}
                            >
                                {getHeroIcon("image", { width: "20px" })}
                                <input
                                    type="file"
                                    ref={imageInputRef}
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                    style={{ display: "none" }}
                                />
                            </button>
                            <button
                                onClick={openYoutubeModal}
                                title="Youtube Video"
                                className={styles.button}
                            >
                                <YoutubeOutlined />
                            </button>
                        </>
                    )}

                    {/* Theme toggle */}
                    {featuresToShow.includes("theme") && (
                        <>
                            <button
                                onClick={toggleTheme}
                                title={
                                    darkMode
                                        ? "Switch to Light Mode"
                                        : "Switch to Dark Mode"
                                }
                                className={`${styles.button} ${styles.themeToggle}`}
                            >
                                {darkMode
                                    ? getHeroIcon("moon", { width: "20px" })
                                    : getHeroIcon("sun", { width: "20px" })}
                            </button>
                        </>
                    )}
                </div>
            </BubbleMenu>
            {/* Modals */}
            {featuresToShow.includes("media") && (
                <>
                    <LinkModal
                        isOpen={linkModalOpen}
                        onClose={() => setLinkModalOpen(false)}
                        onConfirm={handleLinkConfirm}
                        initialUrl={
                            editor.isActive("link")
                                ? editor.getAttributes("link").href
                                : ""
                        }
                    />
                    <YoutubeModal
                        isOpen={youtubeModalOpen}
                        onClose={() => setYoutubeModalOpen(false)}
                        onConfirm={handleYoutubeConfirm}
                        initialWidth={width}
                        initialHeight={height}
                    />
                </>
            )}
        </>
    );
}
export default BubbleMenuBar;
